import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, Router, RouteReuseStrategy, RouterModule } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import * as Sentry from '@sentry/angular';

import { environment } from '../environments/environment';
import * as features from '../features';
import { APP_ENVIRONMENT } from './app-environment.token';
import { APP_FEATURES } from './app-features.token';
import { AppComponent } from './app.component';
import { APP_ROUTES } from './app.routes';
import { CommonAuthModule } from './common/auth/auth.module';
import { CommonCMSModule } from './common/cms/cms.module';
import { provideFetchAdaptor } from './common/fetch/fetch.directive';
import { provideFormAdaptor } from './common/form/form.directive';
import { StorageModule } from './common/storage/storage.module';
import { CoreModule } from './core/core.module';
import { API_BASE_URL } from './core/interceptors/base-path.interceptor';
import { HOST_URL } from './core/services/api-host-url.token';
import { AuthModule } from './features/auth/auth.module';
import { ContrastProviderModule } from './shared/a11y-settings/components/contrast-provider.component';
import { AnalyticsModule } from './shared/analytics/analytics.module';
import { CMSResourceFetchAdaptor } from './shared/cms/cms-adaptor';
import { CMSPageFetchAdaptor } from './shared/cms/cms-page-adaptor';
import { ContactUsAdaptor } from './shared/contact-us/contact-us.adaptor';
import { ProfileFetchAdaptor, ProfileUpdateAdaptor } from './shared/profile/profile.adaptor';
import { ReferralFormAdaptor, UploadReferralFormAdaptor } from './shared/referral-form/referral-form.adaptor';
import { AssessmentFetchAdaptor } from './shared/screening/assessment-fetch.adaptor';
import { ScreeningToolFormAdaptor } from './shared/screening/screening-tool-form.adaptor';
import { ScreeningToolsAdaptor } from './shared/screening/screening-tools.adaptor';

const isDevModeOn = !environment.production;

let SentryProviders: Provider[] = [];

if (!isDevModeOn) {
  SentryProviders = [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ];
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    AnalyticsModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    IonicModule.forRoot({
      mode: 'md',
    }),
    ContrastProviderModule,
    CoreModule.forRoot(),
    StorageModule.forRoot({ name: 'YSS', storeName: environment.name }),
    CommonAuthModule.forRoot(),
    CommonCMSModule.forRoot(),
    AuthModule,
    RouterModule.forRoot(APP_ROUTES, { preloadingStrategy: PreloadAllModules }),
  ],
  providers: [
    ...SentryProviders,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },

    { provide: APP_ENVIRONMENT, useValue: environment },
    { provide: HOST_URL, useValue: environment.hostURL },
    { provide: API_BASE_URL, useValue: environment.apiURL },
    { provide: APP_FEATURES, useValue: features },

    provideFormAdaptor(ProfileUpdateAdaptor, true),
    provideFetchAdaptor(ProfileFetchAdaptor, true),

    provideFetchAdaptor(ScreeningToolsAdaptor, true),
    provideFormAdaptor(ScreeningToolFormAdaptor, true),
    provideFetchAdaptor(AssessmentFetchAdaptor, true),

    provideFormAdaptor(ReferralFormAdaptor, true),
    provideFormAdaptor(UploadReferralFormAdaptor, true),

    provideFetchAdaptor(CMSResourceFetchAdaptor, true),
    provideFetchAdaptor(CMSPageFetchAdaptor, true),

    provideFormAdaptor(ContactUsAdaptor, true),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
